import { Icon } from "@/components/Icons";
import logoMark from "@/assets/bdse-logo-mark.png";
import { shortDate } from "@/features/signals/format";
import { SnoozeCapError, type RecipientBrief } from "../types";
import type { RecipientActionMutations } from "../hooks";

/**
 * Recipient magic-link top bar (Reskin Session C): brand mark + link context
 * (assignee, observed date, expiry chip) and the Snooze control. Behavior is
 * unchanged from the shipped build — snooze extends the token expiry, a confirm
 * note shows the new expiry, and the per-link cap shows the honest limit note.
 * Only the visuals move to the sx- system.
 */
export function RecipientTopBar({
  brief,
  actions,
}: {
  brief: RecipientBrief;
  actions: RecipientActionMutations;
}): JSX.Element {
  const snooze = actions.snooze;
  const remaining = Math.max(0, brief.snoozeMax - brief.snoozeUsed);
  const atCap = remaining <= 0;

  return (
    <>
      <div className="sxr-topbar">
        <img className="sxr-logo" src={logoMark} alt="BD Signal Engine" />
        <div className="sxr-tb-meta">
          <span className="sxr-assigned">
            Assigned to <strong>{brief.recipientName}</strong>
          </span>
          <span className="sxr-observed">
            {brief.assignedAt
              ? `Assigned ${shortDate(brief.assignedAt)}`
              : `Observed ${shortDate(brief.observedAt)}`}
          </span>
          <span className="sxr-expiry">Expires in {brief.expiresInDays} days</span>
          <button
            type="button"
            className="sx-btn sx-btn-p3 sx-btn-quiet"
            onClick={() => snooze.mutate()}
            disabled={snooze.isPending || atCap}
          >
            <Icon name="clock" />
            {snooze.isPending ? "Snoozing…" : "Snooze"}
          </button>
          <span
            className={`sxr-snooze-budget${atCap ? " sxr-snooze-budget--empty" : ""}`}
          >
            {atCap ? "No snoozes left" : `${remaining} of ${brief.snoozeMax} left`}
          </span>
        </div>
      </div>

      {snooze.isSuccess && snooze.data ? (
        <div className="sxr-snooze-note sxr-snooze-note--ok" role="status">
          <Icon name="check" />
          Snoozed. This link now expires {shortDate(snooze.data.expiresAt)}.
        </div>
      ) : snooze.isError ? (
        <div className="sxr-snooze-note sxr-snooze-note--warn" role="status">
          {snooze.error instanceof SnoozeCapError
            ? `Snooze limit reached. Ask ${brief.assignerName} to re-share if you need more time.`
            : "Could not snooze this link right now. Try again shortly."}
        </div>
      ) : null}
    </>
  );
}
